import { ATTR_EXPERIENCED_NAME, ATTR_PRESENT_NAME } from '../constants';
import { Annotation, Attribute, AttributeValue } from '../types/responseTypes';
import {
  ChangeAttributeParams,
  FormAttributeValue,
  ReduceAttributeChangeResult,
} from '../types/types';

export type AttributeValueForSave = Omit<AttributeValue, 'value'> & {
  value: boolean | string | number | undefined;
};

export const buildAttributeValues = (
  attributes: Attribute[],
  formAttributes: FormAttributeValue[],
  isAttributeVisible: (name: string) => boolean
): AttributeValueForSave[] => {
  return attributes.map((attribute) => {
    const formAttribute = formAttributes.find((m) => m.name === attribute.attributeName);
    const isVisible = isAttributeVisible(attribute.attributeName);
    if (isVisible && formAttribute) {
      return {
        attributeId: attribute.attributeId,
        name: attribute.attributeName,
        value: formAttribute.isUnknown ? undefined : formAttribute.value,
        supportedIndirectly: formAttribute.supportedIndirectly || false,
        unknown: formAttribute.isUnknown || false,
      };
    }
    return {
      attributeId: attribute.attributeId,
      name: attribute.attributeName,
      value: undefined,
      supportedIndirectly: false,
      unknown: false,
    };
  });
};

export const getInitializeFormAttributes = (
  attributes: Attribute[] | undefined,
  annotation: Annotation | null
): FormAttributeValue[] => {
  if (!attributes) return [];

  const initialized = attributes.map((attribute) => {
    const existingValue = annotation?.attributes.find((m) => m.name === attribute.attributeName);
    return existingValue
      ? {
          ...existingValue,
          value: existingValue.value,
          isUnknown: existingValue.unknown || false, // Read unknown from backend
        }
      : {
          attributeId: attribute.attributeId,
          name: attribute.attributeName,
          value: undefined,
          supportedIndirectly: false,
          isUnknown: false,
        };
  });

  return initialized;
};

export const reduceAttributeChange = ({
  prev,
  attributes,
  name,
  updates,
}: ChangeAttributeParams): ReduceAttributeChangeResult => {
  const sourceAttribute = prev.find((m) => m.name === name);

  const sourceValue = sourceAttribute?.value;

  const newValue = updates.value !== undefined ? updates.value : sourceValue;

  const isPresentBeingSetToFalse = name === ATTR_PRESENT_NAME && newValue === false;

  const isExperiencedBeingSetToFalse = name === ATTR_EXPERIENCED_NAME && newValue === false;

  const next = attributes.map((attribute) => {
    const existing = prev.find((m) => m.name === attribute.attributeName);

    // ⭐ Changed attribute
    if (attribute.attributeName === name) {
      const updated = existing
        ? { ...existing, ...updates }
        : {
            attributeId: attribute.attributeId,
            name: attribute.attributeName,
            value: undefined,
            supportedIndirectly: false,
            isUnknown: false,
            ...updates,
          };

      if (updated.isUnknown === true) {
        updated.value = undefined;
      }

      if (attribute.type === 'boolean' && updated.value === false) {
        updated.supportedIndirectly = false;
      }

      if (attribute.type === 'boolean' && updated.supportedIndirectly === true) {
        updated.value = true;
        updated.isUnknown = false;
      }

      return updated;
    }

    // ⭐ Present false → wipe all others
    if (isPresentBeingSetToFalse && attribute.attributeName !== ATTR_PRESENT_NAME) {
      return {
        attributeId: attribute.attributeId,
        name: attribute.attributeName,
        value: undefined,
        supportedIndirectly: false,
        isUnknown: false,
      };
    }

    // ⭐ Experienced false → wipe rest
    if (
      isExperiencedBeingSetToFalse &&
      attribute.attributeName !== ATTR_PRESENT_NAME &&
      attribute.attributeName !== ATTR_EXPERIENCED_NAME
    ) {
      return {
        attributeId: attribute.attributeId,
        name: attribute.attributeName,
        value: undefined,
        supportedIndirectly: false,
        isUnknown: false,
      };
    }

    // ⭐ default keep / init
    return (
      existing || {
        attributeId: attribute.attributeId,
        name: attribute.attributeName,
        value: undefined,
        supportedIndirectly: false,
        isUnknown: false,
      }
    );
  });

  return {
    next,
    clearEvidence: isPresentBeingSetToFalse,
  };
};

export const isSaveable = (
  original: AttributeValue[],
  current: FormAttributeValue[],
  attributes: Attribute[]
) => {
  if (!attributes) return false;
  // Check all required attributes have value or unknown selected
  // Only check attributes that are currently visible/displayed

  // Check if any existing attribute has changed
  const hasChanged = original.some((orig) => {
    const curr = current.find((m) => m.name === orig.name);
    if (!curr) return false; // Attribute was removed
    return (
      (curr.value ?? null) !== (orig.value ?? null) ||
      curr.supportedIndirectly !== orig.supportedIndirectly ||
      (curr.isUnknown || false) !== (orig.unknown || false)
    );
  });

  if (hasChanged) return true;

  // Check if any new attribute has been set to a value (including false)
  // This catches attributes that weren't in the original annotation but now have a value
  const hasNewAttribute = current.some((curr) => {
    // Check if the attribute has a meaningful value (not just undefined)
    // For booleans, both true and false are valid values
    const hasValue = curr.value !== undefined;
    const isMarkedUnknown = curr.isUnknown === true;

    if (!hasValue && !isMarkedUnknown) return false; // No value set

    const orig = original.find((m) => m.name === curr.name);

    return !orig;
  });

  return hasNewAttribute;
};
